/**
 * Inline Price Breakdown — dashboard port of the toolkit module
 * (homerunner-toolkit `includes/InlinePriceBreakdown/`, `assets/js/
 * inline-price-breakdown.js`, #1064 Wave 3), plus the shared row model that
 * consolidates every breakdown surface (booking inline, price-details modal,
 * checkout `PriceOverview`, confirmation `CostBreakdown`) onto one bucketing
 * and row-assembly implementation.
 *
 * Lives in `common/` because three separately-bundled widget entries consume
 * it (build-time import, the `common/property-card` precedent). The module is
 * PURE — no React, no DOM: `PriceBreakdownView.tsx` renders the row model
 * this file builds.
 *
 * Unlike the WP module — a sitewide toolkit option, on for every booking
 * widget once the module is enabled — the dashboard inline breakdown is an
 * OPT-IN per-feed/per-widget display setting: `resolvePriceBreakdown()`
 * returns null (feature off) unless `enabled` is explicitly true. The
 * `enabled` leaf IS the port of the module's on/off switch, and it
 * deliberately sidesteps the Wave 2 "which leaf arms it" problem (enum
 * Selects can't be returned to unset, so inferring intent from them
 * misfires — see `resolveFeeLines`' `isConfigured` docblock): a boolean the
 * operator flipped is unambiguous in both directions, and a widget-level
 * `enabled: false` over a feed-global `enabled: true` is a real per-widget
 * off switch (which `feeLines`, lacking such a leaf, cannot express).
 */
import {
  FEE_ITEM_TYPES,
  normalize,
  resolveFeeLines,
  type ResolvedFeeLineConfig,
} from '../../widgets/booking/fee-line-separator'

/**
 * The 5-bucket invoice-item taxonomy every breakdown surface shares. The
 * arrays are byte-identical to the WP plugin's `BookingWidget.php` grouping
 * (verified against origin/main :347-373 for the W3 spec) — the modal has
 * always used them, and W3 standardizes checkout + confirmation on the same
 * scheme. `FEE_ITEM_TYPES` stays DEFINED in `fee-line-separator.ts` (Wave 2's
 * single source for "what counts as a fee") and is re-exported here so the
 * bucketing and the fee-line reshape can never drift apart.
 */
export const RENT_ITEM_TYPES: readonly string[] = [
  'ACCOMMODATION_FARE', 'EXTRA_GUESTS_FEE', 'EXTRA_GUESTS_FARE',
  'DISCOUNT', 'COUPON_DISCOUNT', 'STAY_DISCOUNT', 'PROMOTION',
]
export const MANUAL_ITEM_TYPES: readonly string[] = ['MANUAL', 'ADDON']
export const TAX_ITEM_TYPES: readonly string[] = ['TAX', 'CLEANING_FEE_TAX', 'FEE_TAX']
export const REFUNDABLE_ITEM_TYPES: readonly string[] = ['SECURITY_DEPOSIT']
export { FEE_ITEM_TYPES }

export type PriceBucket = 'rent' | 'fee' | 'manual' | 'tax' | 'refundable'

/**
 * Group labels, verbatim from the WP plugin (and the modal, which has always
 * rendered exactly these). Grouped-mode rows emit them as row labels, where
 * the caller's `relabel` (priceLabelReplacements) can rename them — the same
 * path WP takes, piping its group labels through the label-replacement
 * filter.
 */
export const BUCKET_GROUP_LABELS: Record<PriceBucket, string> = {
  rent: 'Rent:',
  fee: 'Fee(s):',
  manual: 'Add-on(s):',
  refundable: 'Others:',
  tax: 'Tax(es):',
}

/**
 * First-match-wins across the five arrays (WP `bucketOf`,
 * inline-price-breakdown.js:138-154). Unmatched types return null — the WP
 * module silently drops them; surfaces that must never hide a charge
 * (checkout, confirmation) opt into keeping them via
 * `BuildRowsOptions.includeUnknown`.
 */
export function bucketOf(type: string): PriceBucket | null {
  if (RENT_ITEM_TYPES.includes(type)) return 'rent'
  if ((FEE_ITEM_TYPES as readonly string[]).includes(type)) return 'fee'
  if (MANUAL_ITEM_TYPES.includes(type)) return 'manual'
  if (TAX_ITEM_TYPES.includes(type)) return 'tax'
  if (REFUNDABLE_ITEM_TYPES.includes(type)) return 'refundable'
  return null
}

/**
 * Structural item shape, deliberately widget-agnostic: booking/checkout each
 * carry their own `Quote` type and confirmation normalizes snake_case
 * `invoice_items` — all satisfy this. `type` is a plain string (not the
 * booking union) so a runtime-divergent type value flows to the
 * unknown-bucket handling instead of being a type lie.
 */
export interface BreakdownItem {
  title: string
  amount: number
  type: string
}

/** Raw `priceBreakdown` widget-setting shape (mirrors the zod group in
 * `widgets/booking/config.ts`; declared structurally here so `common/` never
 * imports a widget config). Every leaf optional, no defaults — defaulting
 * lives in `resolvePriceBreakdown()` (feed-global-clobber rule, same as
 * `feeLines`). */
export interface PriceBreakdownSetting {
  enabled?: boolean
  style?: 'visible' | 'accordion'
  grouping?: 'itemized' | 'grouped'
  rentStyle?: 'perNight' | 'title'
  showTaxes?: boolean
  hideDeposit?: boolean
  position?: 'above' | 'below'
  hideDetailsButton?: boolean
  heading?: string
  accordionLabel?: string
  openByDefault?: boolean
  taxDisplay?: 'itemized' | 'grouped'
  taxLabel?: string
  explanations?: { from?: string; to?: string }[]
}

export interface ResolvedPriceBreakdownConfig {
  style: 'visible' | 'accordion'
  grouping: 'itemized' | 'grouped'
  rentStyle: 'perNight' | 'title'
  showTaxes: boolean
  showDeposit: boolean
  position: 'above' | 'below'
  hideDetailsButton: boolean
  /** Trimmed; '' = no heading. Visible style only (WP parity: the accordion
   * ignores it — the toggle is the heading). */
  heading: string
  /** ONE label for both toggle states, verbatim (WP `accordion_label` quirk,
   * deliberate — see Settings.php: a noun phrase like "What you pay" can't be
   * verb-prefixed). '' = the state-reflecting Show/Hide defaults. */
  accordionLabel: string
  openByDefault: boolean
  taxDisplay: 'itemized' | 'grouped'
  taxLabel: string
  /** Normalized fee title/fragment -> explanation text. Null-prototype map —
   * looked up with arbitrary PMS-authored titles, same hazard as
   * `resolveFeeLines`' relabel map. */
  explanations: Record<string, string>
}

/**
 * Resolve the raw `priceBreakdown` widget setting into the config the
 * renderers consume. Single point of use for every default (the schema
 * carries none — a materialized schema default would clobber the feed-global
 * layer through getFinalSettings' leaf merge, the #1191 class; the save-path
 * half is `stripBlankPriceBreakdown` in `src/lib/widget-settings-prep.ts`).
 *
 * Returns null — feature off, nothing renders, the details-modal button
 * keeps its current behavior — unless `enabled` is explicitly true.
 * Defaults mirror the WP module's (`Settings.php`), with one deliberate
 * naming translation: WP `rent_style` 'per_night' → `rentStyle` 'perNight'.
 */
/**
 * Disclosure key for one ROW. Keying on the normalized title alone collided:
 * a rate plan with two "Cleaning Fee" items (a common PMS base + per-pet split)
 * shared one open-state, so opening the explanation on either flipped both —
 * and every untitled item keyed the empty string. The index disambiguates.
 */
function rowKey(title: string | undefined, index: number, scope = 'row'): string {
  return `${scope}#${index}#${normalize(title ?? '')}`
}

export function resolvePriceBreakdown(
  settings: { priceBreakdown?: PriceBreakdownSetting | null },
): ResolvedPriceBreakdownConfig | null {
  const pb = settings.priceBreakdown
  if (!pb || pb.enabled !== true) return null

  // Null-prototype + proto-key refusal, exactly the `resolveFeeLines` relabel
  // treatment: keys are matched against PMS-authored fee titles.
  const explanations: Record<string, string> = Object.create(null)
  for (const pair of pb.explanations ?? []) {
    const from = normalize(pair?.from ?? '')
    const to = (pair?.to ?? '').trim()
    if (!from || !to) continue
    if (from === '__proto__' || from === 'constructor' || from === 'prototype') continue
    explanations[from] = to
  }

  return {
    style: pb.style === 'accordion' ? 'accordion' : 'visible',
    grouping: pb.grouping === 'grouped' ? 'grouped' : 'itemized',
    rentStyle: pb.rentStyle === 'title' ? 'title' : 'perNight',
    showTaxes: pb.showTaxes === true,
    // Deposit shows unless explicitly hidden (WP show_deposit default). The
    // raw leaf is hideDeposit — inverted from WP's name so the admin form's
    // untouched (unchecked) checkbox reads the same as this runtime default;
    // the resolved config keeps the WP-parity showDeposit name internally.
    showDeposit: pb.hideDeposit !== true,
    position: pb.position === 'below' ? 'below' : 'above',
    hideDetailsButton: pb.hideDetailsButton === true,
    heading: (pb.heading ?? '').trim(),
    accordionLabel: (pb.accordionLabel ?? '').trim(),
    openByDefault: pb.openByDefault === true,
    taxDisplay: pb.taxDisplay === 'grouped' ? 'grouped' : 'itemized',
    taxLabel: (pb.taxLabel ?? '').trim() || 'Taxes',
    explanations,
  }
}

/**
 * Admin-authored explanation for a fee title, or null. Exact normalized match
 * first, then "contains" (rule key inside the title), first rule wins — the
 * same forgiving matching Fee Line Separator uses for names (WP
 * `explanationFor`). Matches on the item's OWN title, never the displayed
 * label (which may be per-night math for the rent line).
 */
export function explanationFor(
  title: string,
  explanations: Record<string, string>,
): string | null {
  const norm = normalize(title)
  if (!norm) return null
  const exact = explanations[norm]
  if (typeof exact === 'string' && exact !== '') return exact
  for (const key of Object.keys(explanations)) {
    if (key && norm.includes(key)) {
      const text = explanations[key]
      if (typeof text === 'string' && text !== '') return text
    }
  }
  return null
}

export interface BookingGlobalDisplayConfig {
  /** Resolved Fee Line Separator config, or null (off / unreadable). */
  feeLines: ResolvedFeeLineConfig | null
  /** Resolved Inline Price Breakdown config, or null (off / unreadable). */
  breakdown: ResolvedPriceBreakdownConfig | null
  /** Exact-match title relabeler from priceLabelReplacements. A rule applies
   * only when its `to` is a non-blank string — a kept-in-progress row
   * (`{from: 'Cleaning Fee', to: ''}`) or a junk non-string `to` must never
   * erase or replace a charge title. */
  relabel: (title: string) => string
}

/**
 * Resolve the feed-wide global Booking blob (`feed.additional_info.booking`)
 * into the display configs the checkout and confirmation surfaces consolidate
 * onto (#1064 Wave 3).
 *
 * The blob is RAW API JSON — unlike the booking widget's options it never
 * passed a zod parse, so nothing about its shape can be trusted
 * (`feeLines.names: "x"`, `priceBreakdown.explanations: {}`, numeric labels —
 * all real possibilities from hand-edited or legacy blobs). The resolvers
 * assume schema-parsed shapes, so each one runs behind a guard here and a
 * throw degrades to null: junk config means the presentation FEATURE quietly
 * stays off, never that the checkout page crashes. (Fail-loudly governs the
 * money numbers themselves — see `buildBreakdownRows` — not the optional
 * display sugar around them.)
 */
export function resolveBookingGlobalDisplay(blob: unknown): BookingGlobalDisplayConfig {
  const bg = (blob && typeof blob === 'object' && !Array.isArray(blob)
    ? blob
    : {}) as {
    feeLines?: unknown
    priceBreakdown?: unknown
    priceLabelReplacements?: unknown
  }

  let feeLines: ResolvedFeeLineConfig | null = null
  try {
    feeLines = resolveFeeLines({ feeLines: bg.feeLines as Parameters<typeof resolveFeeLines>[0]['feeLines'] })
  } catch {
    feeLines = null
  }

  let breakdown: ResolvedPriceBreakdownConfig | null = null
  try {
    breakdown = resolvePriceBreakdown({ priceBreakdown: bg.priceBreakdown as PriceBreakdownSetting })
  } catch {
    breakdown = null
  }

  const rules = Array.isArray(bg.priceLabelReplacements) ? bg.priceLabelReplacements : []
  const relabel = (title: string): string => {
    for (const rule of rules) {
      if (!rule || typeof rule !== 'object') continue
      const { from, to } = rule as { from?: unknown; to?: unknown }
      if (from === title && typeof to === 'string' && to.trim() !== '') return to
    }
    return title
  }

  return { feeLines, breakdown, relabel }
}

export interface ItemRow {
  kind: 'item'
  /** 'unknown' only ever appears with `includeUnknown` (checkout /
   * confirmation surfaces, where dropping an unrecognized charge would hide
   * money the guest is about to pay). */
  bucket: PriceBucket | 'unknown'
  /** The item's own title, untouched — display relabeling is the view's job. */
  title: string
  amount: number
  explanation: string | null
  /** Stable open-state key for the explanation disclosure (normalized title). */
  expKey: string
  /** Present ONLY on the ACCOMMODATION_FARE row in perNight rent style with
   * nights known: the view renders `price(perNight) × N night(s)` instead of
   * the title. Structured (not preformatted) so currency formatting stays
   * with the renderer. */
  rentMath?: { perNight: number; nights: number }
}

export interface TaxClusterRow {
  kind: 'taxCluster'
  label: string
  total: number
  items: { title: string; amount: number; explanation: string | null; expKey: string }[]
}

export interface GroupRow {
  kind: 'group'
  bucket: PriceBucket
  label: string
  amount: number
}

export type BreakdownRow = ItemRow | TaxClusterRow | GroupRow

export interface BuildRowsOptions {
  /** Per-bucket overrides for the grouped-mode group-row labels (a widget's
   *  translated strings); unset buckets keep BUCKET_GROUP_LABELS. */
  groupLabels?: Partial<Record<PriceBucket, string>>
  grouping: 'itemized' | 'grouped'
  rentStyle: 'perNight' | 'title'
  showTaxes: boolean
  showDeposit: boolean
  taxDisplay: 'itemized' | 'grouped'
  taxLabel: string
  explanations: Record<string, string>
  /** 0/unknown disables the per-night rent math (WP `nights > 0` gate). */
  nights: number
  /**
   * 'payload' (default): itemized rows keep the invoice-item order — WP
   * inline parity, including the deliberate quirk that grouped-tax hold-backs
   * re-render after ALL other rows (deposit included).
   * 'bucketed': rows re-ordered rent → fee → manual → refundable → tax
   * (payload order kept within a bucket) — the modal's and checkout's
   * presentation order.
   */
  order?: 'payload' | 'bucketed'
  /** Keep unmatched item types as plain rows (before taxes) instead of the
   * WP behavior of silently dropping them. */
  includeUnknown?: boolean
}

/**
 * Assemble the breakdown row model from a rateplan's (post-`reshapeRatePlan`)
 * invoice items. Pure port of WP `buildRows`
 * (inline-price-breakdown.js:258-332):
 *
 *  - tax items dropped unless `showTaxes`; refundable unless `showDeposit`;
 *  - itemized: one row per item; rent-row per-night math ONLY for
 *    `ACCOMMODATION_FARE` + `rentStyle: 'perNight'` + `nights > 0` (other
 *    rent items keep their titles);
 *  - tax hold-back gated on `taxDisplay: 'grouped'`: 1 held tax renders as a
 *    plain row (a disclosure wrapping a single row is noise), ≥2 as one
 *    summed cluster row with the individual lines nested;
 *  - grouped: one summed row per bucket, group labels, NO explanations (a
 *    combined line has no single fee to explain);
 *  - explanations resolve against the item's own title.
 *
 * Zero rows = feature has nothing to say; callers must render nothing (the
 * WP "never insert an empty shell" rule).
 */
export function buildBreakdownRows(
  items: readonly BreakdownItem[],
  opts: BuildRowsOptions,
): BreakdownRow[] {
  const {
    grouping, rentStyle, showTaxes, showDeposit, taxDisplay, taxLabel,
    explanations, nights, order = 'payload', includeUnknown = false,
  } = opts

  // 'bucketed' re-orders the walk itself, so every downstream rule (tax
  // hold-back, unknown handling) sees items in presentation order. Unknown
  // types slot between refundable and tax: an unrecognized charge should
  // read as a charge, not as a tax footnote. In 'payload' order unknowns
  // simply stay where the payload put them.
  const source: readonly BreakdownItem[] = order === 'bucketed'
    ? [
        ...(['rent', 'fee', 'manual', 'refundable'] as const).flatMap((b) =>
          items.filter((item) => bucketOf(item.type) === b),
        ),
        ...items.filter((item) => bucketOf(item.type) === null),
        ...items.filter((item) => bucketOf(item.type) === 'tax'),
      ]
    : items

  const rows: BreakdownRow[] = []
  const sums: Record<PriceBucket, number> = { rent: 0, fee: 0, manual: 0, tax: 0, refundable: 0 }
  const seen: Record<PriceBucket, boolean> = { rent: false, fee: false, manual: false, tax: false, refundable: false }
  // Tax lines held back for the grouped-tax cluster. A different feature from
  // `grouping: 'grouped'`, which sums EVERY bucket into one row apiece — when
  // that is on, the tax bucket is already a single row and there is nothing
  // left to collapse.
  const taxItems: BreakdownItem[] = []
  // Grouped mode has no in-loop item rows to slot unknowns among, so they are
  // held and emitted between the refundable and tax groups — the same slot
  // the bucketed contract gives them.
  const groupedUnknownRows: ItemRow[] = []

  // Amount coercion is a bare Number(): a malformed amount (`'1,250.00'` →
  // NaN) propagates NaN into the row and any sum it touches, rendering a loud
  // "$NaN" exactly as every pre-Wave-3 surface already did — NEVER a silent
  // `|| 0`, which would state a real charge as $0.00 on a money surface (the
  // fail-loudly rule; same class `reshapeRatePlan`'s non-finite bail guards).
  // A null amount coerces to 0, also matching the pre-Wave-3 surfaces.
  const coerce = (value: unknown) => Number(value)

  for (const [i, item] of source.entries()) {
    const bucket = bucketOf(item.type)
    if (!bucket) {
      // Unmatched types: WP silently drops them; checkout/confirmation keep
      // them visible (`includeUnknown`) — an unrecognized charge must read
      // as a charge.
      if (includeUnknown) {
        const row: ItemRow = {
          kind: 'item', bucket: 'unknown', title: item.title ?? '',
          amount: coerce(item.amount),
          explanation: explanationFor(item.title ?? '', explanations),
          expKey: rowKey(item.title, i),
        }
        if (grouping === 'grouped') groupedUnknownRows.push(row)
        else rows.push(row)
      }
      continue
    }
    if (bucket === 'tax' && !showTaxes) continue
    if (bucket === 'refundable' && !showDeposit) continue

    const amount = coerce(item.amount)
    sums[bucket] += amount
    seen[bucket] = true

    if (grouping !== 'grouped') {
      if (bucket === 'tax' && taxDisplay === 'grouped') {
        taxItems.push(item)
        continue
      }
      const row: ItemRow = {
        kind: 'item', bucket, title: item.title ?? '', amount,
        explanation: explanationFor(item.title ?? '', explanations),
        expKey: rowKey(item.title, i),
      }
      if (bucket === 'rent' && item.type === 'ACCOMMODATION_FARE' && rentStyle === 'perNight' && nights > 0) {
        row.rentMath = { perNight: amount / nights, nights }
      }
      rows.push(row)
    }
  }

  if (taxItems.length === 1) {
    // Lands here — after every other line, including the refundable deposit —
    // rather than at its payload position, because the bucket is only
    // resolved once the loop ends. Deliberate WP quirk, kept.
    const only = taxItems[0]
    rows.push({
      kind: 'item', bucket: 'tax', title: only.title ?? '',
      amount: coerce(only.amount),
      explanation: explanationFor(only.title ?? '', explanations),
      expKey: normalize(only.title),
    })
  } else if (taxItems.length > 1) {
    rows.push({
      kind: 'taxCluster', label: taxLabel, total: sums.tax,
      items: taxItems.map((item, ti) => ({
        title: item.title ?? '', amount: coerce(item.amount),
        explanation: explanationFor(item.title ?? '', explanations),
        // Namespaced: cluster children are indexed within the cluster, so they
        // can't collide with a top-level row that happens to share an index.
        expKey: rowKey(item.title, ti, 'tax'),
      })),
    })
  }

  if (grouping === 'grouped') {
    for (const bucket of ['rent', 'fee', 'manual', 'refundable', 'tax'] as const) {
      // The walk always reaches the tax slot, so held unknown rows always
      // land there — between the refundable and tax groups, per the contract.
      if (bucket === 'tax' && groupedUnknownRows.length > 0) rows.push(...groupedUnknownRows)
      if (seen[bucket]) {
        rows.push({ kind: 'group', bucket, label: opts.groupLabels?.[bucket] ?? BUCKET_GROUP_LABELS[bucket], amount: sums[bucket] })
      }
    }
  }

  return rows
}
